import { motion } from "framer-motion";
import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ChatStore } from "@/stores/ChatStore";
import SettingsStore from "@/stores/SettingsStore";
import { fetchSessionId } from "@/services/schaden-ki/fetch_session_id";
import { Button } from "@/components/ui/button";

export default function SessionTimeoutOverlay() {
  const { t } = useTranslation();
  const sessionTimedOut = ChatStore(state => state.sessionTimedOut);
  const messages = ChatStore(state => state.messages);
  const setIsRunning = ChatStore(state => state.setIsRunning);
  const setIsHistoryPanelOpen = ChatStore(state => state.setIsHistoryPanelOpen);
  const resetMessages = ChatStore(state => state.resetMessages);
  const displayHistory = SettingsStore(state => state.displayHistory);
  const useSchadenKI = SettingsStore(state => state.useSchadenKI);

  if (!sessionTimedOut) return null;

  const hasHistory = messages.length > 0;

  const startSession = (preserveHistory: boolean) => {
    if (!preserveHistory) {
      resetMessages();
    }

    // Fresh start always needs a new SchadenKI session. Continue-with-history
    // keeps the existing one, but fetches if it was never cached.
    if (useSchadenKI && (!preserveHistory || !ChatStore.getState().sessionId)) {
      fetchSessionId().catch(err => {
        console.error("Error fetching session ID:", err);
      });
    }

    // Clear timeout UI first and drop any leftover force-token so remounted
    // idle hooks cannot immediately re-trigger the timeout overlay.
    ChatStore.setState({ sessionTimedOut: false, idleTimeoutForceToken: 0 });
    setIsRunning(true);
    setIsHistoryPanelOpen(displayHistory || preserveHistory);
  };

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-slate-50/95 to-slate-100/95 backdrop-blur-sm">
      <motion.div
        className="flex flex-col items-center gap-6 p-10 max-w-md text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <motion.div
          className="flex items-center justify-center w-20 h-20 rounded-full bg-sky-50 border border-sky-200"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.4, delay: 0.1, type: "spring", stiffness: 200 }}
        >
          <Clock className="w-10 h-10 text-sky-600" strokeWidth={1.5} />
        </motion.div>

        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold text-slate-800 tracking-tight">
            {t("sessionTimeout.title")}
          </h1>
          <p className="text-base text-slate-500 leading-relaxed">
            {t("sessionTimeout.message")}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {hasHistory && (
            <Button
              size="lg"
              onClick={() => startSession(true)}
              className="min-w-[180px]"
            >
              {t("sessionTimeout.continueWithHistory")}
            </Button>
          )}
          <Button
            size="lg"
            variant={hasHistory ? "outline" : "default"}
            onClick={() => startSession(false)}
            className="min-w-[180px]"
          >
            {t("sessionTimeout.startFresh")}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
